import { CommonModule, NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';

type TechnicalVariantIdentifier = number | string;

type MaybeArray<T> = T | T[];

interface TechnicalVariantAsset {
  id?: TechnicalVariantIdentifier;
  detail?: string;
  bets?: string[];
  type?: string;
  focus?: string;
  categories?: MaybeArray<string>;
  length?: MaybeArray<string>;
  format?: MaybeArray<string>;
}

interface FormatDisplay {
  type: string;
  ratio: string;
}

interface TechnicalVariantUpdate {
  asset: TechnicalVariantAsset;
  index: number;
}

@Component({
  selector: 'mtm-edit-asset-technical-variants',
  templateUrl: './edit-asset-technical-variants.component.html',
  styleUrls: ['./edit-asset-technical-variants.component.scss', '../../edit-asset-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NgbDropdownModule, NgClass],
})
export class MtmEditAssetTechnicalVariantsComponent {
  @Input() tempAssets: TechnicalVariantAsset[] = [];
  @Input() forVariant: boolean = false;
  @Input() submitted: boolean = false;
  @Input() betsOptions: string[] = [];
  @Input() types: string[] = [];
  @Input() focuses: string[] = [];
  @Input() availableCategories: string[] = [];
  @Input() lengthOptions: string[] = [];
  @Input() formatOptions: string[] = [];
  @Input() formatDisplayMap: Record<string, FormatDisplay> = {};

  @Output() assetUpdated: EventEmitter<TechnicalVariantUpdate> = new EventEmitter<TechnicalVariantUpdate>();
  @Output() delete: EventEmitter<TechnicalVariantIdentifier> = new EventEmitter<TechnicalVariantIdentifier>();
  @Output() addVariant: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('tableContainer') tableContainer?: ElementRef<HTMLDivElement>;
  @ViewChildren(NgbDropdown) dropdowns?: QueryList<NgbDropdown>;

  public scrollToBottom(): void {
    if (!this.tableContainer?.nativeElement) {
      return;
    }

    setTimeout(() => {
      const element: HTMLDivElement = this.tableContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    }, 0);
  }

  public handleScroll(): void {
    this.closeAllDropdowns();
  }

  private closeAllDropdowns(): void {
    const { dropdowns }: { dropdowns?: QueryList<NgbDropdown> } = this;
    if (!dropdowns) {
      return;
    }

    for (const dropdown of dropdowns) {
      dropdown.close();
    }
  }

  public getFormatDisplay(format: string): string {
    if (!format) {
      return '';
    }

    const formatInfo: FormatDisplay | undefined = this.formatDisplayMap?.[format];
    if (!formatInfo) {
      return format;
    }

    return `${formatInfo.type} <strong>${formatInfo.ratio}</strong>`;
  }

  public onTitleChange(asset: TechnicalVariantAsset, index: number, value: string): void {
    asset.detail = value;
    this.emitAssetUpdated(asset, index);
  }

  public onBetsChange(asset: TechnicalVariantAsset, index: number, value: string): void {
    const currentBets: string[] = asset.bets ? [...asset.bets] : [];
    asset.bets = currentBets.includes(value)
      ? currentBets.filter((bet: string) => bet !== value)
      : [...currentBets, value];
    this.emitAssetUpdated(asset, index);
  }

  public onTypeChange(asset: TechnicalVariantAsset, index: number, value: string): void {
    asset.type = value;
    this.emitAssetUpdated(asset, index);
  }

  public onFocusChange(asset: TechnicalVariantAsset, index: number, value: string): void {
    asset.focus = value;
    this.emitAssetUpdated(asset, index);
  }

  public onCategoryChange(asset: TechnicalVariantAsset, index: number, value: string): void {
    asset.categories = [value];
    this.emitAssetUpdated(asset, index);
  }

  public onLengthChange(asset: TechnicalVariantAsset, index: number, value: string): void {
    asset.length = [value];
    this.emitAssetUpdated(asset, index);
  }

  public onFormatChange(asset: TechnicalVariantAsset, index: number, value: string): void {
    asset.format = [value];
    this.emitAssetUpdated(asset, index);
  }

  public onDelete(asset: TechnicalVariantAsset, index: number): void {
    this.delete.emit(this.getIdentifier(asset, index));
  }

  public onAddVariant(): void {
    this.addVariant.emit();
  }

  public trackByAsset(index: number, asset: TechnicalVariantAsset): TechnicalVariantIdentifier {
    return asset?.id ?? index;
  }

  private getIdentifier(asset: TechnicalVariantAsset, index: number): TechnicalVariantIdentifier {
    return asset?.id ?? index;
  }

  private emitAssetUpdated(asset: TechnicalVariantAsset, index: number): void {
    this.assetUpdated.emit({ asset, index });
  }
}
